"use client";

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";

type HeaderScrollHideContextValue = {
	setSuppressScrollHide: (suppress: boolean) => void;
};

const HeaderScrollHideContext = createContext<HeaderScrollHideContextValue | null>(null);

/**
 * While `true`, the header stays visible even when the user scrolls down (auto-hide is suppressed).
 * Used when the globe locale menu or sheet is open so the popover is not clipped or lost.
 * Returns a no-op when used outside `HeaderScrollShadow` (e.g. Storybook) so callers do not need guards.
 */
export function useHeaderScrollHideSuppression(): (suppress: boolean) => void {
	const ctx = useContext(HeaderScrollHideContext);
	return ctx?.setSuppressScrollHide ?? (() => {});
}

export function HeaderScrollShadow({ children }: { children: ReactNode }) {
	const [hidden, setHidden] = useState(false);
	const [suppressScrollHide, setSuppressScrollHide] = useState(false);
	const lastScrollY = useRef(0);

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY;

			// Always show header near the top
			if (currentScrollY < 10) {
				setHidden(false);
			} else if (currentScrollY > lastScrollY.current + 5) {
				// Scrolling down — hide (with 5px deadzone to prevent jitter)
				setHidden(true);
			} else if (currentScrollY < lastScrollY.current - 5) {
				// Scrolling up — show
				setHidden(false);
			}

			lastScrollY.current = currentScrollY;
		};
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const headerHidden = hidden && !suppressScrollHide;

	return (
		<HeaderScrollHideContext.Provider value={{ setSuppressScrollHide }}>
			<header
				className={`pointer-events-none sticky top-0 z-40 px-3 py-3 transition-transform duration-300 sm:px-6 sm:py-[18px] ${
					headerHidden ? "-translate-y-full" : "translate-y-0"
				}`}
			>
				<div className="pointer-events-auto">{children}</div>
			</header>
		</HeaderScrollHideContext.Provider>
	);
}
